import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { useSesion } from '../sesion/ProveedorDeSesion';
import {
  DATOS_FISCALES_INICIALES,
  DatosFiscalesDelComercio,
  type PaisDelAlta,
} from '../componentes/DatosFiscalesDelComercio';
import { paqueteDePais } from '@actualgold/localizacion';

type EstadoDeInvitacion = 'pendiente' | 'aceptada' | 'revocada' | 'vencida';

interface Invitacion {
  readonly id: string;
  readonly correo: string;
  readonly comercio: string | null;
  readonly notas: string | null;
  readonly creadaEn: string;
  readonly expiraEn: string;
  readonly estado: EstadoDeInvitacion;
}

interface PerfilDeNegocio {
  readonly codigo: string;
  readonly claveDeTraduccion: string;
}

/** La identificación como se escribe en el país del comercio: `76.123.456-0`, no `761234560`. */
function identificacionVisible(comercio: Comercio): string {
  const paquete = paqueteDePais(comercio.paisCodigo);
  const numero =
    paquete?.formatearIdentificacion(comercio.tipoDeIdentificacion, comercio.identificacion) ??
    comercio.identificacion;
  return `${comercio.tipoDeIdentificacion} ${numero}`;
}

const COMERCIO_VACIO = {
  nombre: '',
  razonSocial: '',
  ...DATOS_FISCALES_INICIALES,
  perfilDeNegocio: '',
  sucursal: '',
  correoDelDuenio: '',
  nombreDelDuenio: '',
  claveDelDuenio: '',
};

interface Duenio {
  readonly usuarioId: string;
  readonly nombre: string;
  readonly correo: string;
}

/** Una clave provisoria legible: sin caracteres que se confundan al dictarla por teléfono. */
function claveProvisoriaAlAzar(): string {
  const alfabeto = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => alfabeto[byte % alfabeto.length] ?? 'x').join('');
}

interface Comercio {
  readonly id: string;
  readonly nombre: string;
  readonly razonSocial: string;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string;
  readonly paisCodigo: string;
  readonly domicilio: string | null;
  readonly ciudad: string | null;
  readonly codigoPostal: string | null;
  readonly telefono: string | null;
  readonly correo: string | null;
  readonly activa: boolean;
  readonly creadaEn: string;
}

/** Lo que la plataforma puede corregirle a un comercio. La identificación no está, a propósito. */
const CAMPOS_DE_LA_FICHA = [
  'nombre',
  'razonSocial',
  'domicilio',
  'ciudad',
  'codigoPostal',
  'telefono',
  'correo',
] as const;
type CampoDeLaFicha = (typeof CAMPOS_DE_LA_FICHA)[number];
const ETIQUETA_DEL_CAMPO: Record<CampoDeLaFicha, string> = {
  nombre: 'alta.nombre',
  razonSocial: 'alta.razonSocial',
  domicilio: 'plataforma.domicilio',
  ciudad: 'plataforma.ciudad',
  codigoPostal: 'plataforma.codigoPostal',
  telefono: 'plataforma.telefono',
  correo: 'plataforma.correoDelComercio',
};

interface Administrador {
  readonly id: string;
  readonly correo: string;
  readonly nombre: string;
}

const TONO_DEL_ESTADO: Record<EstadoDeInvitacion, 'bien' | 'atencion' | 'neutro' | 'mal'> = {
  pendiente: 'atencion',
  aceptada: 'bien',
  revocada: 'neutro',
  vencida: 'mal',
};

/**
 * La administración de la instalación.
 *
 * ## Qué es y qué no es
 *
 * Es la pantalla de quien decide **quién entra**: emite invitaciones a fundar un comercio, ve en
 * qué estado están y las revoca. No es una pantalla «de arriba de todo»: quien administra la
 * plataforma **no ve los datos de ningún comercio**, y las políticas de la base no tienen
 * excepción para él. Esa garantía se le puede dar por escrito a un cliente, y esta pantalla no la
 * afloja: todo lo que muestra es del plano de plataforma.
 *
 * ## Por qué el enlace se muestra en pantalla
 *
 * Porque no se mandan correos todavía, y hasta acá el enlace de una invitación **quedaba sólo en el
 * registro del servidor**. En una máquina de desarrollo es cómodo; en un servidor convierte al
 * registro en algo tan sensible como la base. Mostrarlo acá es la misma decisión que Gente y roles
 * toma para las suyas: se pasa por donde sea, sin depender de que ningún correo haya salido.
 */
export function Plataforma(): React.ReactElement {
  const { t } = useTranslation();
  const { perfil } = useSesion();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const [invitando, setInvitando] = useState(false);
  const [formulario, setFormulario] = useState({ correo: '', comercio: '', notas: '' });
  const [enlace, setEnlace] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invitaciones = useQuery({
    queryKey: ['plataforma', 'invitaciones'],
    queryFn: () => pedir<Invitacion[]>('/plataforma/invitaciones'),
  });
  const comercios = useQuery({
    queryKey: ['plataforma', 'empresas'],
    queryFn: () => pedir<Comercio[]>('/plataforma/empresas'),
  });
  const administradores = useQuery({
    queryKey: ['plataforma', 'administradores'],
    queryFn: () => pedir<Administrador[]>('/plataforma/administradores'),
  });

  /** El alta en un paso: el comercio y su dueño. Al terminar, los datos para entrar, una sola vez. */
  const [creandoComercio, setCreandoComercio] = useState(false);
  const [nuevo, setNuevo] = useState(COMERCIO_VACIO);
  const [errorDeCreacion, setErrorDeCreacion] = useState<string | null>(null);
  const [creado, setCreado] = useState<{
    correo: string;
    clave: string;
    conCuenta: boolean;
  } | null>(null);
  const opciones = useQuery({
    queryKey: ['empresas', 'opciones'],
    enabled: creandoComercio,
    queryFn: () =>
      pedir<{ paises: PaisDelAlta[]; perfiles: PerfilDeNegocio[] }>('/empresas/opciones'),
  });
  // El perfil se deriva: si no se eligió ninguno, el primero de la lista. Copiarlo al estado
  // cuando llegan las opciones deja un instante con el selector mostrando uno y el estado vacío.
  const perfilEfectivo =
    nuevo.perfilDeNegocio === ''
      ? (opciones.data?.perfiles[0]?.codigo ?? '')
      : nuevo.perfilDeNegocio;

  const crearComercio = useMutation({
    mutationFn: () =>
      pedir<{ empresaId: string; duenioCreado: boolean }>('/plataforma/empresas', {
        method: 'POST',
        cuerpo: {
          empresa: {
            nombre: nuevo.nombre.trim(),
            razonSocial: nuevo.razonSocial.trim(),
            tipoDeIdentificacion: nuevo.tipoDeIdentificacion,
            identificacion: nuevo.identificacion.trim(),
            condicionIva: nuevo.condicionIva,
            paisCodigo: nuevo.paisCodigo,
            jurisdiccionCodigo:
              nuevo.jurisdiccionCodigo === '' ? undefined : nuevo.jurisdiccionCodigo,
            perfilDeNegocio: perfilEfectivo,
            monedaBase: nuevo.monedaBase,
            sucursal: {
              codigo: 'CENTRO',
              nombre:
                nuevo.sucursal.trim() === '' ? t('alta.sucursalPorDefecto') : nuevo.sucursal.trim(),
            },
          },
          duenio: {
            correo: nuevo.correoDelDuenio.trim(),
            nombre: nuevo.nombreDelDuenio.trim(),
            clave: nuevo.claveDelDuenio,
          },
        },
      }),
    onSuccess: async (respuesta) => {
      setErrorDeCreacion(null);
      setCreado({
        correo: nuevo.correoDelDuenio.trim().toLowerCase(),
        clave: nuevo.claveDelDuenio,
        conCuenta: !respuesta.duenioCreado,
      });
      setNuevo(COMERCIO_VACIO);
      await cola.invalidateQueries({ queryKey: ['plataforma', 'empresas'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeCreacion(textoDeError(fallo));
    },
  });

  /** El comercio cuyo dueño se está restableciendo, y lo que salió: una provisoria o un enlace. */
  const [restableciendo, setRestableciendo] = useState<Comercio | null>(null);
  const [provisoria, setProvisoria] = useState(claveProvisoriaAlAzar);
  const [resultadoDeClave, setResultadoDeClave] = useState<
    | { tipo: 'provisoria'; correo: string; clave: string }
    | { tipo: 'enlace'; correo: string; enlace: string }
    | null
  >(null);
  const [errorDeClave, setErrorDeClave] = useState<string | null>(null);
  const duenios = useQuery({
    queryKey: ['plataforma', 'duenios', restableciendo?.id],
    enabled: restableciendo !== null,
    queryFn: () => pedir<Duenio[]>(`/plataforma/empresas/${restableciendo?.id ?? ''}/duenios`),
  });
  const ponerProvisoria = useMutation({
    mutationFn: (duenio: Duenio) =>
      pedir<void>(
        `/plataforma/empresas/${restableciendo?.id ?? ''}/duenios/${duenio.usuarioId}/clave-provisoria`,
        { method: 'POST', cuerpo: { clave: provisoria } },
      ).then(() => duenio),
    onSuccess: (duenio) => {
      setErrorDeClave(null);
      setResultadoDeClave({ tipo: 'provisoria', correo: duenio.correo, clave: provisoria });
    },
    onError: (fallo: unknown) => {
      setErrorDeClave(textoDeError(fallo));
    },
  });
  const generarEnlace = useMutation({
    mutationFn: (duenio: Duenio) =>
      pedir<{ token: string }>(
        `/plataforma/empresas/${restableciendo?.id ?? ''}/duenios/${duenio.usuarioId}/enlace-de-recuperacion`,
        { method: 'POST' },
      ).then((respuesta) => ({ duenio, token: respuesta.token })),
    onSuccess: ({ duenio, token }) => {
      setErrorDeClave(null);
      setResultadoDeClave({
        tipo: 'enlace',
        correo: duenio.correo,
        enlace: `${globalThis.location.origin}/recuperacion?token=${token}`,
      });
    },
    onError: (fallo: unknown) => {
      setErrorDeClave(textoDeError(fallo));
    },
  });

  /** El comercio que se está editando, y lo escrito encima de su ficha. */
  const [editando, setEditando] = useState<Comercio | null>(null);
  const [edicion, setEdicion] = useState<Partial<Record<CampoDeLaFicha, string>>>({});
  const [errorDeEdicion, setErrorDeEdicion] = useState<string | null>(null);
  const valorDe = (campo: CampoDeLaFicha): string =>
    edicion[campo] ?? String(editando?.[campo] ?? '');
  const editar = useMutation({
    mutationFn: () => {
      // Sólo lo que cambió, como toda corrección desde E15.
      const cambios: Partial<Record<CampoDeLaFicha, string>> = {};
      for (const campo of CAMPOS_DE_LA_FICHA) {
        const escrito = edicion[campo];
        if (escrito !== undefined && escrito.trim() !== String(editando?.[campo] ?? '')) {
          cambios[campo] = escrito.trim();
        }
      }
      return pedir<void>(`/plataforma/empresas/${editando?.id ?? ''}`, {
        method: 'PATCH',
        cuerpo: cambios,
      });
    },
    onSuccess: async () => {
      setEditando(null);
      setEdicion({});
      setErrorDeEdicion(null);
      await cola.invalidateQueries({ queryKey: ['plataforma', 'empresas'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeEdicion(textoDeError(fallo));
    },
  });

  /** El comercio que se está por borrar, y la identificación escrita para confirmar. */
  const [borrando, setBorrando] = useState<string | null>(null);
  const [confirmacion, setConfirmacion] = useState('');
  const [errorDeBorrado, setErrorDeBorrado] = useState<string | null>(null);
  const borrar = useMutation({
    mutationFn: (id: string) =>
      pedir<void>(`/plataforma/empresas/${id}`, {
        method: 'DELETE',
        cuerpo: { identificacion: confirmacion.trim() },
      }),
    onSuccess: async () => {
      setBorrando(null);
      setConfirmacion('');
      setErrorDeBorrado(null);
      await cola.invalidateQueries({ queryKey: ['plataforma', 'empresas'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeBorrado(textoDeError(fallo));
    },
  });

  /** El comercio que se está por suspender: la confirmación va en la fila, no en un diálogo. */
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const fijarActiva = useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) =>
      pedir<void>(`/plataforma/empresas/${id}`, { method: 'PATCH', cuerpo: { activa } }),
    onSuccess: async () => {
      setConfirmando(null);
      await cola.invalidateQueries({ queryKey: ['plataforma', 'empresas'] });
    },
  });

  const invitar = useMutation({
    mutationFn: () =>
      pedir<{ id: string; token: string }>('/plataforma/invitaciones', {
        method: 'POST',
        cuerpo: {
          correo: formulario.correo.trim(),
          ...(formulario.comercio.trim() === '' ? {} : { comercio: formulario.comercio.trim() }),
          ...(formulario.notas.trim() === '' ? {} : { notas: formulario.notas.trim() }),
        },
      }),
    onSuccess: async (respuesta) => {
      // La misma ruta que abre quien recibe el enlace: es la que el guión de humo recorre.
      setEnlace(`${globalThis.location.origin}/fundacion?token=${respuesta.token}`);
      setError(null);
      setFormulario({ correo: '', comercio: '', notas: '' });
      await cola.invalidateQueries({ queryKey: ['plataforma', 'invitaciones'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const revocar = useMutation({
    mutationFn: (id: string) => pedir<void>(`/plataforma/invitaciones/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['plataforma', 'invitaciones'] });
    },
  });

  const columnas: Columna<Invitacion>[] = [
    {
      clave: 'correo',
      titulo: t('plataforma.correo'),
      contenido: (fila) => (
        <div>
          <div>{fila.correo}</div>
          <div className="text-xs text-texto-tenue">
            {fila.comercio ?? t('plataforma.sinComercio')}
          </div>
        </div>
      ),
    },
    {
      clave: 'emitida',
      titulo: t('plataforma.emitida'),
      contenido: (fila) => new Date(fila.creadaEn).toLocaleDateString(),
    },
    {
      clave: 'vence',
      titulo: t('plataforma.vence'),
      contenido: (fila) => new Date(fila.expiraEn).toLocaleDateString(),
    },
    {
      clave: 'estado',
      titulo: t('plataforma.estado'),
      contenido: (fila) => (
        <Insignia tono={TONO_DEL_ESTADO[fila.estado]}>
          {t(`plataforma.estados.${fila.estado}`)}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      // Sólo lo pendiente se revoca: una aceptada ya creó una cuenta y revocarla no desharía nada
      // —el servidor lo rechaza—, y una vencida o revocada ya no sirve para nadie.
      contenido: (fila) =>
        fila.estado !== 'pendiente' ? null : (
          <div className="flex justify-end">
            <Boton
              cargando={revocar.isPending}
              onClick={() => {
                revocar.mutate(fila.id);
              }}
            >
              {t('plataforma.revocar')}
            </Boton>
          </div>
        ),
    },
  ];

  const columnasDeComercios: Columna<Comercio>[] = [
    {
      clave: 'nombre',
      titulo: t('plataforma.nombre'),
      contenido: (fila) => (
        <div>
          <div>{fila.nombre}</div>
          <div className="text-xs text-texto-tenue">{fila.razonSocial}</div>
        </div>
      ),
    },
    {
      clave: 'identificacion',
      titulo: t('plataforma.identificacion'),
      contenido: (fila) => identificacionVisible(fila),
    },
    {
      clave: 'fundado',
      titulo: t('plataforma.fundado'),
      contenido: (fila) => new Date(fila.creadaEn).toLocaleDateString(),
    },
    {
      clave: 'estado',
      titulo: t('plataforma.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activa ? 'bien' : 'mal'}>
          {fila.activa ? t('plataforma.activo') : t('plataforma.suspendido')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        borrando === fila.id ? (
          // Borrar: sólo suspendido, y escribiendo la identificación. Se dice qué va a pasar y se
          // confirma en la misma fila, para que se vea a quién.
          <div className="flex flex-col items-end gap-2">
            <p className="text-xs text-texto-tenue">{t('plataforma.ayudaBorrar')}</p>
            <Campo
              etiqueta={t('plataforma.escribiLaIdentificacion')}
              value={confirmacion}
              onChange={(evento) => {
                setConfirmacion(evento.target.value);
              }}
            />
            {errorDeBorrado === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {errorDeBorrado}
              </p>
            )}
            <div className="flex gap-2">
              <Boton
                tono="peligro"
                cargando={borrar.isPending}
                disabled={confirmacion.trim() === ''}
                onClick={() => {
                  borrar.mutate(fila.id);
                }}
              >
                {t('plataforma.borrarDefinitivamente')}
              </Boton>
              <Boton
                tono="secundario"
                onClick={() => {
                  setBorrando(null);
                  setConfirmacion('');
                  setErrorDeBorrado(null);
                }}
              >
                {t('acciones.cancelar')}
              </Boton>
            </div>
          </div>
        ) : !fila.activa ? (
          <div className="flex justify-end gap-2">
            <Boton
              onClick={() => {
                setEditando(fila);
                setEdicion({});
                setErrorDeEdicion(null);
              }}
            >
              {t('plataforma.editar')}
            </Boton>
            <Boton
              cargando={fijarActiva.isPending}
              onClick={() => {
                fijarActiva.mutate({ id: fila.id, activa: true });
              }}
            >
              {t('plataforma.reactivar')}
            </Boton>
            {/* El botón de borrar aparece **sólo** en un comercio suspendido: el servidor lo
                rechaza igual en uno activo, y ofrecerlo para que rebote es enseñar a no leer. */}
            <Boton
              tono="sutil"
              onClick={() => {
                setBorrando(fila.id);
                setConfirmacion('');
                setErrorDeBorrado(null);
              }}
            >
              {t('plataforma.borrar')}
            </Boton>
          </div>
        ) : confirmando === fila.id ? (
          // Suspender saca a gente que está trabajando: se dice qué va a pasar y se confirma en
          // la misma fila, para que se vea a quién.
          <div className="flex flex-col items-end gap-2">
            <p className="text-xs text-texto-tenue">{t('plataforma.confirmarSuspension')}</p>
            <div className="flex gap-2">
              <Boton
                tono="peligro"
                cargando={fijarActiva.isPending}
                onClick={() => {
                  fijarActiva.mutate({ id: fila.id, activa: false });
                }}
              >
                {t('plataforma.suspender')}
              </Boton>
              <Boton
                tono="secundario"
                onClick={() => {
                  setConfirmando(null);
                }}
              >
                {t('acciones.cancelar')}
              </Boton>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Boton
              onClick={() => {
                setEditando(fila);
                setEdicion({});
                setErrorDeEdicion(null);
              }}
            >
              {t('plataforma.editar')}
            </Boton>
            <Boton
              onClick={() => {
                setRestableciendo(fila);
                setResultadoDeClave(null);
                setErrorDeClave(null);
                setProvisoria(claveProvisoriaAlAzar());
              }}
            >
              {t('plataforma.restablecerClave')}
            </Boton>
            <Boton
              onClick={() => {
                setConfirmando(fila.id);
              }}
            >
              {t('plataforma.suspender')}
            </Boton>
          </div>
        ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('plataforma.titulo')}
        descripcion={t('plataforma.descripcion')}
        acciones={
          <>
            <Boton
              tono="principal"
              onClick={() => {
                setCreandoComercio(true);
                setCreado(null);
                setErrorDeCreacion(null);
              }}
            >
              {t('plataforma.nuevoComercio')}
            </Boton>
            <Boton
              onClick={() => {
                setInvitando(true);
                setEnlace(null);
                setError(null);
              }}
            >
              {t('plataforma.invitar')}
            </Boton>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <p className="text-apoyo text-texto-tenue">
          {t('plataforma.quienSos')} {perfil?.correo ?? ''}
        </p>

        <Tarjeta titulo={t('plataforma.comercios')} descripcion={t('plataforma.ayudaComercios')}>
          {comercios.isPending ? <Cargando /> : null}
          {comercios.isSuccess ? (
            <Tabla
              columnas={columnasDeComercios}
              filas={comercios.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio titulo={t('plataforma.sinComercios')} />}
            />
          ) : null}
        </Tarjeta>

        <Tarjeta
          titulo={t('plataforma.invitaciones')}
          descripcion={t('plataforma.ayudaInvitaciones')}
        >
          {invitaciones.isPending ? <Cargando /> : null}
          {invitaciones.isError ? (
            <Problema
              error={invitaciones.error}
              reintentar={() => {
                void invitaciones.refetch();
              }}
            />
          ) : null}
          {invitaciones.isSuccess ? (
            <Tabla
              columnas={columnas}
              filas={invitaciones.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio titulo={t('plataforma.sinInvitaciones')} />}
            />
          ) : null}
        </Tarjeta>

        <Tarjeta
          titulo={t('plataforma.administradores')}
          descripcion={t('plataforma.ayudaAdministradores')}
        >
          {administradores.isPending ? <Cargando /> : null}
          {administradores.isSuccess ? (
            <ul className="flex flex-col divide-y divide-borde-tenue text-cuerpo">
              {administradores.data.map((persona) => (
                <li key={persona.id} className="flex items-center gap-3 py-2">
                  <span className="font-medium">{persona.nombre}</span>
                  <span className="text-apoyo text-texto-tenue">{persona.correo}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Tarjeta>
      </div>

      <Dialogo
        abierto={restableciendo !== null}
        titulo={t('plataforma.restablecerClave')}
        descripcion={t('plataforma.ayudaClaveDelDuenio')}
        onCerrar={() => {
          setRestableciendo(null);
          setResultadoDeClave(null);
          setErrorDeClave(null);
        }}
      >
        {resultadoDeClave !== null ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              {resultadoDeClave.tipo === 'provisoria'
                ? t('plataforma.provisoriaPuesta')
                : t('plataforma.enlaceListo')}
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-texto-tenue">{t('plataforma.correo')}</dt>
              <dd>
                <code className="text-xs">{resultadoDeClave.correo}</code>
              </dd>
              <dt className="text-texto-tenue">
                {resultadoDeClave.tipo === 'provisoria' ? t('clave.nueva') : t('plataforma.enlace')}
              </dt>
              <dd>
                <code className="break-all text-xs">
                  {resultadoDeClave.tipo === 'provisoria'
                    ? resultadoDeClave.clave
                    : resultadoDeClave.enlace}
                </code>
              </dd>
            </dl>
          </div>
        ) : duenios.isPending ? (
          <Cargando />
        ) : (
          <div className="flex flex-col gap-4">
            {(duenios.data ?? []).length === 0 ? (
              <Vacio titulo={t('plataforma.sinDuenios')} />
            ) : null}
            {(duenios.data ?? []).map((duenio) => (
              <div
                key={duenio.usuarioId}
                className="flex flex-col gap-3 rounded-marco border border-borde p-3"
              >
                <div>
                  <div className="font-medium">{duenio.nombre}</div>
                  <div className="text-xs text-texto-tenue">{duenio.correo}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <Campo
                    etiqueta={t('plataforma.claveProvisoria')}
                    ayuda={t('plataforma.ayudaClaveProvisoria')}
                    value={provisoria}
                    onChange={(evento) => {
                      setProvisoria(evento.target.value);
                    }}
                  />
                  <div className="flex gap-2">
                    <Boton
                      tono="principal"
                      cargando={ponerProvisoria.isPending}
                      onClick={() => {
                        ponerProvisoria.mutate(duenio);
                      }}
                    >
                      {t('plataforma.aplicar')}
                    </Boton>
                    <Boton
                      tono="secundario"
                      onClick={() => {
                        setProvisoria(claveProvisoriaAlAzar());
                      }}
                    >
                      {t('plataforma.generarOtra')}
                    </Boton>
                  </div>
                </div>
                <div className="flex flex-col gap-1 border-t border-borde-tenue pt-3">
                  <p className="text-apoyo text-texto-tenue">
                    {t('plataforma.ayudaEnlaceDeRecuperacion')}
                  </p>
                  <div className="flex">
                    <Boton
                      cargando={generarEnlace.isPending}
                      onClick={() => {
                        generarEnlace.mutate(duenio);
                      }}
                    >
                      {t('plataforma.enlaceDeRecuperacion')}
                    </Boton>
                  </div>
                </div>
              </div>
            ))}
            {errorDeClave === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {errorDeClave}
              </p>
            )}
          </div>
        )}
      </Dialogo>

      <Dialogo
        abierto={editando !== null}
        titulo={t('plataforma.editarComercio')}
        descripcion={t('plataforma.ayudaEditarComercio')}
        onCerrar={() => {
          setEditando(null);
          setEdicion({});
          setErrorDeEdicion(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={editar.isPending}
            onClick={() => {
              editar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-apoyo text-texto-tenue">
            {editando === null ? null : identificacionVisible(editando)}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {CAMPOS_DE_LA_FICHA.map((campo) => (
              <Campo
                key={campo}
                etiqueta={t(ETIQUETA_DEL_CAMPO[campo])}
                value={valorDe(campo)}
                onChange={(evento) => {
                  setEdicion({ ...edicion, [campo]: evento.target.value });
                }}
              />
            ))}
          </div>
          {errorDeEdicion === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {errorDeEdicion}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={creandoComercio}
        titulo={t('plataforma.nuevoComercio')}
        descripcion={t('plataforma.ayudaNuevoComercio')}
        onCerrar={() => {
          setCreandoComercio(false);
          setCreado(null);
          setErrorDeCreacion(null);
        }}
        acciones={
          creado === null ? (
            <Boton
              tono="principal"
              cargando={crearComercio.isPending}
              onClick={() => {
                crearComercio.mutate();
              }}
            >
              {t('plataforma.crear')}
            </Boton>
          ) : undefined
        }
      >
        {creado !== null ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              {creado.conCuenta
                ? t('plataforma.comercioCreadoConCuenta')
                : t('plataforma.comercioCreado')}
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-texto-tenue">{t('plataforma.entrarEn')}</dt>
              <dd>
                <code className="text-xs">{globalThis.location.origin}</code>
              </dd>
              <dt className="text-texto-tenue">{t('plataforma.correo')}</dt>
              <dd>
                <code className="text-xs">{creado.correo}</code>
              </dd>
              {creado.conCuenta ? null : (
                <>
                  <dt className="text-texto-tenue">{t('plataforma.claveDelDuenio')}</dt>
                  <dd>
                    <code className="text-xs">{creado.clave}</code>
                  </dd>
                </>
              )}
            </dl>
          </div>
        ) : opciones.isPending ? (
          <Cargando />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
              {t('plataforma.elComercio')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta={t('alta.nombre')}
                obligatorio
                value={nuevo.nombre}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, nombre: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('alta.razonSocial')}
                obligatorio
                value={nuevo.razonSocial}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, razonSocial: evento.target.value });
                }}
              />
            </div>
            <DatosFiscalesDelComercio
              paises={opciones.data?.paises ?? []}
              valor={nuevo}
              alCambiar={(fiscales) => {
                setNuevo({ ...nuevo, ...fiscales });
              }}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Selector
                etiqueta={t('alta.perfil')}
                value={perfilEfectivo}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, perfilDeNegocio: evento.target.value });
                }}
              >
                {(opciones.data?.perfiles ?? []).map((perfil) => (
                  <option key={perfil.codigo} value={perfil.codigo}>
                    {t(perfil.claveDeTraduccion, { defaultValue: perfil.codigo })}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta={t('plataforma.sucursal')}
                placeholder={t('alta.sucursalPorDefecto')}
                value={nuevo.sucursal}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, sucursal: evento.target.value });
                }}
              />
            </div>

            <p className="mt-1 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
              {t('plataforma.elDuenio')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta={t('plataforma.correoDelDuenio')}
                type="email"
                obligatorio
                value={nuevo.correoDelDuenio}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, correoDelDuenio: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('plataforma.nombreDelDuenio')}
                obligatorio
                value={nuevo.nombreDelDuenio}
                onChange={(evento) => {
                  setNuevo({ ...nuevo, nombreDelDuenio: evento.target.value });
                }}
              />
            </div>
            <Campo
              etiqueta={t('plataforma.claveDelDuenio')}
              ayuda={t('plataforma.ayudaClaveDelDuenio')}
              type="password"
              obligatorio
              value={nuevo.claveDelDuenio}
              onChange={(evento) => {
                setNuevo({ ...nuevo, claveDelDuenio: evento.target.value });
              }}
            />
            {errorDeCreacion === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {errorDeCreacion}
              </p>
            )}
          </div>
        )}
      </Dialogo>

      <Dialogo
        abierto={invitando}
        titulo={t('plataforma.invitar')}
        descripcion={t('plataforma.ayudaInvitar')}
        onCerrar={() => {
          setInvitando(false);
          setEnlace(null);
          setError(null);
        }}
        acciones={
          enlace === null ? (
            <Boton
              tono="principal"
              cargando={invitar.isPending}
              onClick={() => {
                invitar.mutate();
              }}
            >
              {t('plataforma.emitir')}
            </Boton>
          ) : undefined
        }
      >
        {enlace === null ? (
          <div className="flex flex-col gap-3">
            <Campo
              etiqueta={t('plataforma.correo')}
              type="email"
              obligatorio
              value={formulario.correo}
              onChange={(evento) => {
                setFormulario({ ...formulario, correo: evento.target.value });
              }}
            />
            <Campo
              etiqueta={t('plataforma.comercio')}
              ayuda={t('plataforma.ayudaComercio')}
              value={formulario.comercio}
              onChange={(evento) => {
                setFormulario({ ...formulario, comercio: evento.target.value });
              }}
            />
            <Campo
              etiqueta={t('plataforma.notas')}
              value={formulario.notas}
              onChange={(evento) => {
                setFormulario({ ...formulario, notas: evento.target.value });
              }}
            />
            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm">{t('plataforma.invitacionLista')}</p>
            <code
              aria-label={t('plataforma.enlace')}
              className="break-all rounded-marco border border-borde bg-lienzo p-2 text-xs"
            >
              {enlace}
            </code>
          </div>
        )}
      </Dialogo>
    </>
  );
}
